import React, { useState, useEffect } from 'react';
import { X, Download, ZoomIn, ZoomOut, Loader } from 'lucide-react';
import { Button } from '../ui/button';
import { getAuthToken } from '@/utils/auth';

interface DocumentViewerProps {
  url?: string;
  fileName?: string;
  isOpen: boolean;
  onClose: () => void;
  doctorId?: string; // Optional: if provided, fetch through backend
  documentId?: string; // Optional: specific document ID
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({
  url: initialUrl,
  fileName = 'Documento',
  isOpen,
  onClose,
  doctorId,
  documentId,
}) => {
  const [zoom, setZoom] = useState(100);
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [actualFileName, setActualFileName] = useState(fileName);

  useEffect(() => {
    if (!isOpen) {
      setDisplayUrl(null);
      setError(null);
      setLoading(false);
      return;
    }

    const loadDocument = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (doctorId && documentId) {
          // Fetch specific document view URL
          const token = getAuthToken();
          const viewUrl = `${import.meta.env.VITE_API_BASE_URL}/doctors/${doctorId}/documents/${documentId}/view-url`;
          
          const response = await fetch(viewUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Documento não encontrado');
          }

          const result = await response.json();
          if (!result.success || !result.data?.signedUrl) {
            throw new Error(result.error || 'URL de visualização inválida');
          }

          setDisplayUrl(result.data.signedUrl);
          setActualFileName(result.data.fileName || fileName);
        } else if (doctorId) {
          // Fetch legacy professional document preview
          const token = getAuthToken();
          const previewUrl = `${import.meta.env.VITE_API_BASE_URL}/doctors/${doctorId}/professional-documents/preview`;
          
          console.log('[DocumentViewer] Fetching preview URL...');
          const previewResponse = await fetch(previewUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          });

          console.log('[DocumentViewer] Preview response status:', previewResponse.status);

          if (!previewResponse.ok) {
            const errorData = await previewResponse.json();
            throw new Error(errorData.error || 'Documento não encontrado');
          }

          const previewData = await previewResponse.json();
          console.log('[DocumentViewer] Preview data:', previewData);

          if (!previewData.success || !previewData.documentUrl) {
            throw new Error(previewData.error || 'URL de documento inválida');
          }

          setDisplayUrl(previewData.documentUrl);
          setActualFileName(previewData.fileName || fileName);
        } else if (initialUrl) {
          setDisplayUrl(initialUrl);
          setActualFileName(fileName);
        } else {
          setError('Nenhum documento disponível');
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido';
        console.error('[DocumentViewer] Load error:', errorMsg);
        setError(errorMsg);
        setDisplayUrl(null);
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [isOpen, doctorId, documentId, initialUrl, fileName]);

  if (!isOpen) return null;

  const isPDF = displayUrl?.toLowerCase().includes('.pdf') || displayUrl?.includes('application/pdf') || actualFileName?.toLowerCase().endsWith('.pdf');
  const isImage = displayUrl?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) || actualFileName?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i);

  const getExtension = () => {
    const ext = actualFileName?.split('.')?.pop()?.toLowerCase();
    return ext && ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? ext : 'pdf';
  };

  const handleDownload = async () => {
    if (!displayUrl) return;

    try {
      const token = getAuthToken();
      
      console.log('[DocumentViewer] Download starting');
      const response = await fetch(displayUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Falha ao baixar documento`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const extension = getExtension();
      const baseFileName = actualFileName?.split('.')?.slice(0, -1).join('.') || 'documento';
      link.download = `${baseFileName}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erro desconhecido';
      console.error('[DocumentViewer] Download error:', errorMsg);
      alert(`Erro ao fazer download: ${errorMsg}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 dark:from-neutral-800 dark:to-neutral-900 border-b border-gray-200 dark:border-neutral-700 px-6 py-4 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              Visualizador de Documento
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{actualFileName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-neutral-800 border-b border-gray-200 dark:border-neutral-700 px-6 py-3 shrink-0">
          <div className="flex items-center gap-2">
            {(isPDF || isImage) && !error && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setZoom(Math.max(75, zoom - 10))}
                  className="gap-2"
                >
                  <ZoomOut className="w-4 h-4" />
                  Zoom out
                </Button>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 px-4">
                  {zoom}%
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setZoom(Math.min(200, zoom + 10))}
                  className="gap-2"
                >
                  <ZoomIn className="w-4 h-4" />
                  Zoom in
                </Button>
              </>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownload}
            disabled={loading || !displayUrl || !!error}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            Baixar
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-gray-900 flex items-center justify-center p-4">
          {loading ? (
            <div className="text-center p-6 bg-white dark:bg-neutral-800 rounded-lg">
              <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                Carregando documento...
              </p>
            </div>
          ) : error ? (
            <div className="text-center p-6 bg-white dark:bg-neutral-900 rounded-lg max-w-md border border-red-500/30">
              <p className="text-red-600 dark:text-red-400 mb-4 font-semibold">
                ⚠️ Documento Indisponível
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4 text-sm">
                {error}
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-xs">
                Solução: Faça um novo upload do documento no seu perfil.
              </p>
            </div>
          ) : !displayUrl ? (
            <div className="text-center p-6 bg-white dark:bg-neutral-900 rounded-lg">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Nenhum documento disponível
              </p>
            </div>
          ) : isPDF ? (
            <div className="w-full h-full flex items-center justify-center">
              <iframe
                src={`${displayUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                className="w-full h-full rounded-lg border border-gray-300 bg-white"
                title="PDF Viewer"
                onError={() => setError('Erro ao carregar PDF no visualizador')}
              />
            </div>
          ) : isImage ? (
            <div className="flex items-center justify-center">
              <img
                src={displayUrl}
                alt={actualFileName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: `${zoom}%`,
                  height: 'auto',
                  objectFit: 'contain',
                }}
                className="transition-transform duration-300"
                onError={() => setError('Erro ao carregar imagem')}
              />
            </div>
          ) : (
            <div className="text-center p-6 bg-white dark:bg-neutral-900 rounded-lg">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Tipo de arquivo não suportado para visualização inline.
              </p>
              <Button onClick={handleDownload} className="gap-2">
                <Download className="w-4 h-4" />
                Baixar Arquivo
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
